'use client';
import cn from 'classnames';
import Image from 'next/image';
import { FormikHelpers, FormikErrors } from 'formik';
import toast from 'react-hot-toast';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import InputField from '@/components/form/lib/InputField/InputField';
import SubmitButton from '@/components/form/lib/SubmitButton/SubmitButton';
import { useLoggedInUser } from '@/components/hook/user';
import { isValidEmail } from '@/util/regexp';

import { SubscribeToNewsletterRequest } from '@/app/api/marketing/newsletter/route';

import Arrow from 'public/img/icon/arrow-right-submit.svg';
import './FooterNewsletterSignupForm.style.scss';
import { CACHE_TIME } from '@/const/cache';

export type FooterNewsletterSignupFormValues = {
  email: string;
};

interface FooterNewsletterSignupFormProps
  extends Omit<
    BaseFormProps<FooterNewsletterSignupFormValues>,
    'children' | 'onSubmit'
  > {
  onSubmit?: (
    values: FooterNewsletterSignupFormValues,
    actions: FormikHelpers<FooterNewsletterSignupFormValues>
  ) => void;
}

function defaultValidation(
  values: FooterNewsletterSignupFormValues
): FormikErrors<FooterNewsletterSignupFormValues> {
  const { email } = values;
  const errors: FormikErrors<FooterNewsletterSignupFormValues> = {};

  if (!isValidEmail(email)) {
    errors.email = 'Enter email';
  }

  return errors;
}

export default function FooterNewsletterSignupForm(
  props: FooterNewsletterSignupFormProps
) {
  const {
    className = '',
    initialValues,
    validate = defaultValidation,
    onSubmit,
    // onSubmitSuccess,
    // onSubmitFailure,
    validateOnBlur = false,
    validateOnChange = false
  } = props;

  const user = useLoggedInUser();

  const handleSubmit = async (values: FooterNewsletterSignupFormValues) => {
    const body: SubscribeToNewsletterRequest = {
      email: values.email
    };

    if (user) {
      body.existingUser = {
        id: user.id,
        email: user.email
      };
    }

    try {
      const response = await fetch('/api/marketing/newsletter', {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(body),
        next: { revalidate: CACHE_TIME.NO_CACHE }
      });
      const respJson = await response.json();

      if (!respJson.success) {
        toast.error('An error occurred. Please try again');
      }

      toast.success("Thanks! You've been added to our mailing list");
    } catch {
      toast.error('An error occurred. Please try again');
    }
  };

  return (
    <Form<FooterNewsletterSignupFormValues>
      className={cn('FooterNewsletterSignupForm', className)}
      initialValues={initialValues}
      validate={validate}
      // If an 'onSubmit function' is passed in, that function will override
      // this component's 'handleSubmit' function.
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      <InputField
        className='FooterNewsletterSignupForm-input'
        name='email'
        placeholder='Your email address'
      />
      <SubmitButton
        className='FooterNewsletterSignupForm-submit'
        variant='transparent'
      >
        <Image
          src={Arrow}
          alt=''
          className='arrow-image'
        />
      </SubmitButton>
    </Form>
  );
}
